import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useInViews } from "utility/useInViews";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { ReactComponent as ClockIcon } from "Assets/icon/icon-clock.svg";
import { useCardTracking } from "../../../apps/user/learner/hooks/use-card-tracking";
import { CardVariant, CardSize } from "./enums";

import styles from "./content-card.module.scss";
import { CalenderIconComponent } from "../CalenderIconComponent";
import { useOpenModalFromCard } from "providers/content-modal-manager";
import { Link, useNavigate } from "react-router-dom";

export interface CardItemType {
  id: string;
  __typename: string;
  imageUrl: string;
  title: string;
  subtitle: string;
  tags: string[];
  link: string;
  date?: string;
  duration?: string;
  time?: string;
  isFavourite?: boolean;
}

export interface CardButtonProps {
  icon?: React.ReactNode | string;
  text?: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>, itemId: string) => void;
  ariaLabel?: string;
  className?: string;
  loading?: boolean;
}

interface ContentCardProps {
  item: CardItemType;
  variant: CardVariant;
  actionButtons?: CardButtonProps[];
  size?: CardSize;
  listName?: string;
  listPosition?: number;
  onClickHandler?: () => void;
  /** Fires alongside the card's own generic impression tracking - callers that
   * need extra dimensions (e.g. Ori's Configuration) attach their own event. */
  onImpression?: () => void;
  showBiometrics?: boolean;
  className?: string;
  tabContext?: string;
  widthMode?: "full" | "fixed";
}

const ActionButton: React.FC<CardButtonProps & { itemId: string }> = ({
  icon,
  text,
  onClick,
  ariaLabel,
  className,
  itemId,
  loading = false,
}) => {
  const handleClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClick(e, itemId);
  };

  const baseProps = {
    onClick: handleClick,
    "aria-label": ariaLabel,
    className: clsx(styles.actionButton, className),
    loading,
  };

  if (!!icon && !text) {
    return (
      <span className={styles.buttonIcon}>
        <IconButton {...baseProps} icon={icon} shape="square" />
      </span>
    );
  }

  return (
    <Button
      {...baseProps}
      variant="enclosed-primary"
      monochromeColour="white"
      size="small"
    >
      {text && <span className={styles.buttonText}>{text}</span>}
    </Button>
  );
};

const ContentCard: React.FC<ContentCardProps> = ({
  item,
  variant,
  size = "regular",
  listName,
  listPosition,
  onClickHandler,
  onImpression,
  actionButtons = [],
  showBiometrics = false,
  className,
  tabContext,
  widthMode = "fixed",
}) => {
  const { ref, inView } = useInViews();
  const { logImpression, logClick } = useCardTracking({
    item,
    listName,
    listPosition,
    showBiometrics,
    tabContext,
  });
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  // Held in a ref so an inline callback (a new identity every render) can't
  // re-trigger the effect and double-count impressions.
  const onImpressionRef = useRef(onImpression);
  onImpressionRef.current = onImpression;

  useEffect(() => {
    if (inView) {
      logImpression();
      onImpressionRef.current?.();
    }
  }, [inView, logImpression]);

  const ITEM_TYPES = {
    RECORDING: "LearnerRecording",
    SESSION: "LearnerGroupSession",
  };

  const openModalFromCard = useOpenModalFromCard();

  const handleClick = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (item.__typename === ITEM_TYPES.RECORDING) {
      openModalFromCard({ type: "recording", id: item.id });
    } else if (item.__typename === ITEM_TYPES.SESSION) {
      openModalFromCard({ type: "session", id: item.id });
    } else {
      navigate(item.link);
    }
    logClick();
    if (onClickHandler) {
      onClickHandler();
    }
  };

  const cardClasses = clsx(
    styles.card,
    styles[variant],
    size === "large" && styles.large,
    size === "tall" && styles.tall,
    widthMode === "full" && styles.fullWidth,
    "sans",
    className && className
  );

  const isLargeSize = size === "large" || size === "tall";
  const isImageCard = variant === "image";

  return (
    <Link
      ref={ref}
      to="#"
      onClick={handleClick}
      className={cardClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.imageContainer}>
        <img src={item.imageUrl} alt={item.title} className={styles.image} />

        {actionButtons && actionButtons.length > 0 && (
          <div
            className={clsx(
              {
                [styles.imageActionOverlay]: isImageCard,
                [styles.imageOverlay]: !isImageCard,
              },
              {
                [styles.visible]: isHovered,
              }
            )}
          >
            <div
              className={clsx({
                [styles.imageActionButtons]: isImageCard,
                [styles.actionButtons]: !isImageCard,
              })}
            >
              {actionButtons.slice(0, 2).map((button, index) => (
                <ActionButton key={index} {...button} itemId={item.id} />
              ))}
            </div>
          </div>
        )}

        {isImageCard && (
          <div className={styles.imageContentOverlay}>
            <div className={styles.overlayContent}>
              <div className={styles.info}>
                {item.duration && (
                  <p className={styles.duration}>{item.duration}</p>
                )}
                {item.tags[0] && (
                  <span className={styles.tag}>{item.tags[0]}</span>
                )}
              </div>

              <h3 className={styles.title}>{item.title}</h3>
              {!isLargeSize && (
                <div className={styles.overlayInfo}>
                  <p className={styles.subtitle}>{item.subtitle}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {!isImageCard && (
        <div className={styles.content}>
          <div className={styles.info}>
            <p className={styles.duration}>{item.duration}</p>
            <div
              className={clsx(
                styles.dataContainer,
                item.date && styles.hasDate
              )}
            >
              {item.date && <CalenderIconComponent date={item.date} />}
              <div className={styles.tags}>
                {item.tags.map((tag) => (
                  <span key={tag} className={styles.tag}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <h3 className={styles.title}>{item.title}</h3>
          {item.time && (
            <span className={styles.time}>
              <ClockIcon height={16} width={16} />
              <p className={styles.subtitle}>{item.time}</p>
            </span>
          )}
          <p className={styles.subtitle}>{item.subtitle}</p>
        </div>
      )}
    </Link>
  );
};

export default ContentCard;
